/**
 * The Search screen. The workbook has no equivalent, and BUILD-SPEC 8 calls
 * this the single biggest gain - so it is a first-class tab, not a field
 * hidden on another screen. The index lives in memory (built in app.tsx from
 * the current summary) and every keystroke queries it synchronously; there is
 * no debounce because there is nothing to debounce for.
 */
import { useEffect, useRef, useState } from "preact/hooks";

import { search, type Hit, type SearchIndex } from "./search.js";

export function SearchView({
  index,
  onHit,
}: {
  index: SearchIndex | null;
  onHit: (hit: Hit) => void;
}) {
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Focused on open: the reason you came here is to type.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  if (!index) {
    return (
      <div class="search empty-state">
        <h2>Nothing to search yet</h2>
        <p class="muted">Search works on the published summary. Run the flow on your PC first.</p>
      </div>
    );
  }

  const hits = search(index, q);
  // Sections scan faster than one flat list with tags. Stores and products
  // keep relevance order; receipts read newest first, which is how anyone
  // actually remembers them.
  const groups: { label: string; hits: Hit[] }[] = [
    { label: "Stores", hits: hits.filter((h) => h.kind === "store") },
    { label: "Products", hits: hits.filter((h) => h.kind === "product") },
    {
      label: "Receipts",
      hits: hits
        .filter((h) => h.kind === "receipt")
        .sort((a, b) => (b.date ?? "").localeCompare(a.date ?? "")),
    },
  ].filter((g) => g.hits.length > 0);

  return (
    <div class="search">
      <input
        ref={inputRef}
        class="searchbox"
        type="search"
        placeholder="Products, stores, receipts, notes…"
        value={q}
        onInput={(e) => setQ((e.target as HTMLInputElement).value)}
        aria-label="Search"
      />
      {q.trim() ? (
        groups.length ? (
          groups.map((g) => (
            <section key={g.label}>
              <h3>{g.label}</h3>
              <ul class="rows">
                {g.hits.map((h) => (
                  <li key={`${h.kind}:${h.key}`} class="row">
                    <button type="button" class="rowmain" onClick={() => onHit(h)}>
                      <span class="title">{h.label}</span>
                      <span class="sub">{h.sub}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))
        ) : (
          <p class="muted pad">Nothing matches “{q.trim()}”.</p>
        )
      ) : (
        <p class="muted pad small">
          Free text over every product, store, receipt, line item and note in the ledger.
        </p>
      )}
    </div>
  );
}
